import { Link } from 'react-router-dom';

export interface NewHeroImage {
  src: string;
  alt?: string;
}

interface NewHeroSectionProps {
  images: Array<string | NewHeroImage>;
  imageLayout?: 'auto' | 'single' | 'collage';
}

const defaultAltTexts = [
  '예복 피팅 후기 사진',
  '원단 상담 후기 사진',
  '촬영 예복 후기 사진',
  '수트 후기 사진',
];

function normalizeImages(images: Array<string | NewHeroImage>): NewHeroImage[] {
  return images
    .map((image, index) => {
      if (typeof image === 'string') {
        return {
          src: image,
          alt: defaultAltTexts[index] || '다이렉트웨딩 후기 사진',
        };
      }

      return {
        src: image.src,
        alt: image.alt || defaultAltTexts[index] || '다이렉트웨딩 후기 사진',
      };
    })
    .filter((image) => Boolean(image.src));
}

function HeroImage({ image, className }: { image: NewHeroImage; className: string }) {
  return (
    <div className={`${className} overflow-hidden bg-[#F4F4F5] shadow-[inset_0_0_0_1px_rgba(112,115,124,0.12)]`}>
      <img
        src={image.src}
        alt={image.alt}
        className="block h-full w-full object-cover"
        loading="eager"
      />
    </div>
  );
}

function PlannerFeedbackCard() {
  return (
    <div className="absolute left-8 right-8 bottom-5 rounded-[14px] border border-[rgba(112,115,124,0.22)] bg-white px-4 py-3.5 shadow-[0_2px_8px_rgba(23,23,23,0.08),0_8px_24px_rgba(23,23,23,0.10)] md:left-[-20px] md:right-auto md:bottom-9 md:w-[300px] md:rounded-2xl md:px-5 md:py-[18px]">
      <div className="mb-1.5 flex items-center gap-1.5 md:mb-2.5 md:gap-2">
        <span className="h-[5px] w-[5px] rounded-full bg-[#A84F38] md:h-1.5 md:w-1.5" />
        <span className="text-[11px] font-bold tracking-[0.12em] text-[#A84F38] md:text-[12px]">
          플래너 피드백
        </span>
      </div>
      <p className="m-0 text-[13px] font-medium leading-[1.6] text-[#17171A] md:text-[14px] md:leading-[1.65]">
        &quot;경조사용 맞춤이라면 합리적인 가격대의 반수제(MTM)가 가장 인기 있어요.&quot;
      </p>
    </div>
  );
}

function HeroVisual({
  images,
  imageLayout = 'auto',
}: {
  images: NewHeroImage[];
  imageLayout?: NewHeroSectionProps['imageLayout'];
}) {
  const layout = imageLayout === 'auto'
    ? images.length <= 1
      ? 'single'
      : 'collage'
    : imageLayout;

  if (layout === 'single') {
    const image = images[0];

    if (!image) return null;

    return (
      <section className="relative px-5 pb-11 md:px-0 md:pb-0">
        <div className="relative h-[360px] md:h-[560px]">
          <HeroImage image={image} className="h-full rounded-2xl md:rounded-[20px]" />
          <PlannerFeedbackCard />
        </div>
      </section>
    );
  }

  const [first, second, third, fourth] = images;

  return (
    <section className="relative px-5 pb-11 md:px-0 md:pb-0">
      <div className="relative grid grid-cols-2 gap-2.5 md:block md:h-[560px]">
        <div className="flex flex-col gap-2.5 pt-5 md:absolute md:left-0 md:top-6 md:w-[47%] md:gap-3.5 md:pt-0">
          {first && <HeroImage image={first} className="aspect-[3/4] rounded-2xl md:rounded-[20px]" />}
          {second && <HeroImage image={second} className="aspect-[4/3] rounded-2xl md:rounded-[20px]" />}
        </div>
        <div className="flex flex-col gap-2.5 md:absolute md:right-0 md:top-0 md:w-[47%] md:gap-3.5">
          {third && <HeroImage image={third} className="aspect-[4/3] rounded-2xl md:rounded-[20px]" />}
          {fourth && <HeroImage image={fourth} className="aspect-[3/4] rounded-2xl md:rounded-[20px]" />}
        </div>
        <PlannerFeedbackCard />
      </div>
    </section>
  );
}

export default function NewHeroSection({ images, imageLayout = 'auto' }: NewHeroSectionProps) {
  const normalizedImages = normalizeImages(images);

  return (
    <section className="bg-white">
      <div className="mx-auto grid max-w-[1200px] gap-8 px-0 pb-0 pt-9 md:px-6 md:pb-16 md:pt-16 lg:grid-cols-[minmax(360px,1fr)_minmax(320px,460px)] lg:items-center lg:gap-12 lg:pb-24 lg:pt-[88px]">
        <div className="px-5 md:px-0">
          <span className="mb-4 inline-block rounded-full bg-[#FBF1ED] px-3 py-1.5 text-[12px] font-semibold tracking-[0.0194em] text-[#8E3F2C] md:mb-6 md:px-3.5 md:text-[13px]">
            다이렉트 카페 실제 후기로 검증한 인증업체
          </span>

          <h1 className="m-0 mb-3 text-[32px] font-bold leading-[1.3] tracking-[-0.027em] text-[#17171A] md:mb-5 md:text-[52px] md:leading-[1.22] md:tracking-[-0.0282em]">
            업체부터 고르지 마세요.
            <br />
            <span className="text-[#A84F38]">내 취향</span>부터 찾으세요.
          </h1>

          <p className="m-0 mb-6 text-[15px] leading-[1.65] text-[rgba(46,47,51,0.61)] md:mb-9 md:max-w-[440px] md:text-[17px]">
            몇 가지 질문이면 예복·예물·한복의 선택 기준이 정리돼요.
            <br className="hidden md:block" />
            검증된 인증업체까지 바로 이어드릴게요.
          </p>

          <div className="flex flex-col gap-2.5 md:flex-row md:items-center md:gap-3">
            <Link
              to="/guide"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#A84F38] px-0 py-[15px] text-[16px] font-semibold text-white transition-colors hover:bg-[#8E3F2C] hover:text-white md:px-7"
            >
              3분 만에 내 취향 찾기
              <i className="ri-arrow-right-line text-lg" />
            </Link>
            <Link
              to="/shops/suit"
              className="inline-flex items-center justify-center rounded-lg border border-[rgba(112,115,124,0.22)] px-0 py-3.5 text-[16px] font-medium text-[#17171A] transition-colors hover:bg-[rgba(112,115,124,0.08)] hover:text-[#17171A] md:px-6"
            >
              인증업체 둘러보기
            </Link>
          </div>

          <p className="m-0 mt-3.5 text-center text-[12px] tracking-[0.0194em] text-[rgba(55,56,60,0.4)] md:mt-5 md:text-left md:text-[13px]">
            6가지 질문 · 평균 90초 · 회원가입 없이
          </p>
        </div>

        <HeroVisual images={normalizedImages} imageLayout={imageLayout} />
      </div>
    </section>
  );
}
